import { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  isEmpty,
} from 'lodash';
import {
  API,
  GlobalActions,
  ExpandedLocationsHelper,
  LoadingContent,
} from 'vibeguide';
import LoopDetails from './LoopDetails';
import './LoopSidebar.scss';

class LoopSidebar extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      loop: {
        _id: '',
        companyId: '',
        companyName: '',
        name: '',
        description: '',
        locations: [],
        mediaItems: [],
        messages: [],
        active: true,
        tags: [],
        durationSeconds: 0,
        messageCount: 0,
        locationCount: 0,
      },
    };
  }

  componentDidMount() {
    const {
      loopId,
    } = this.props;

    if (loopId && loopId !== 'new') {
      this.getLoop();
    }
  }

  componentDidUpdate(prevProps) {
    const {
      loopId,
      isNew,
    } = this.props;

    const {
      loopId: prevLoopId,
    } = prevProps;

    if (loopId && !isNew && loopId !== prevLoopId) {
      this.getLoop();
    }
  }

  /**
   * Update the loop
   */
  onUpdate = async (data) => {
    // If messages changed but mediaItems weren't explicitly provided, re-derive them
    // so panels reflecting mediaItems (e.g. Loop Definition) stay in sync with any
    // add/remove/reorder performed from the Self Promo Messages panel.
    if (data.messages && !data.mediaItems) {
      const newMessages = data.messages.filter(message => !isEmpty(message._id));
      data.messages = newMessages;

      data.mediaItems = newMessages.map((message, i) => {
        const media = (message.media && message.media[0]) || {};
        const contentType = media.contentType || '';

        return {
          aspectRatio: '16:9',
          contentType,
          durationSeconds: media.durationSeconds,
          fileName: media.fileName,
          fileSizeBytes: media.fileSizeBytes,
          mediaType: contentType.indexOf('image') !== -1 ? 'image' : 'video',
          messageId: message._id,
          name: message.name,
          resolution: [1920, 1080],
          targetScreen: { resolution: [1920, 1080], orientation: 'landscape' },
          url: media.url,
          _id: i,
        };
      });
    }

    this.setState((state) => {
      return {
        loop: {
          ...state.loop,
          ...data,
        },
      };
    });
  };

  /**
   * Close the sidebar
   */
  onClose = () => {
    const {
      setPanel,
    } = this.props;

    setPanel({
      show: false,
    });
  };

  /**
   * Close the extra sidebar
   */
  onCloseExtraPanel = () => {
    const {
      setPanel,
    } = this.props;

    setPanel({
      extraPanel: {
        show: false,
      },
    });

    // Reset the children after it's been hidden
    setTimeout(() => {
      setPanel({
        extraPanel: {
          children: null,
        },
      });
    }, 900);
  };

  /**
   * Close the third panel on the sidebar
   */
  onCloseThirdPanel = () => {
    const {
      setPanel,
    } = this.props;

    setPanel({
      thirdPanel: {
        show: false,
      },
    });

    // Reset the children after it's been hidden
    setTimeout(() => {
      setPanel({
        thirdPanel: {
          children: null,
        },
      });
    }, 900);
  };

  getLoop = async () => {
    const {
      loopId,
      user,
    } = this.props;

    const loop = await API.Loops.getById(loopId);

    const filteredMediaItems = loop.messages.map((message, i) => {
      message.media[0]._id = i;
      message.media[0].messageId = message._id;
      message.media[0].mediaType = message.media[0].contentType.indexOf('image') !== -1 ? 'image' : 'video';
      message.media[0].name = message.name;
      return message.media[0];
    });

    loop.mediaItems = filteredMediaItems;

    if (!loop) {
      console.warn('Loop ID Not Found', loop);
      return;
    }

    const isACAF = user.isAllLocations({
      companyId: loop.companyId,
      locations: loop.locations,
    });

    // Only fetch expanded locations when all current and future is not selected
    const expandedLocations = isACAF
      ? []
      : await API.Loops.getExpandedLocations(loopId);

    // Set the location data
    loop.locationsData = ExpandedLocationsHelper.getLocationsData(expandedLocations);
    loop.acaf = isACAF;

    this.setState({
      loop,
    });
  };

  render() {
    const {
      loopId,
      isNew,
      redirectTo,
      showDisplayPlayer,
    } = this.props;

    const {
      loop,
    } = this.state;

    return (
      <div className="LoopSidebar">
        <LoopDetails
          loop={loop}
          mediaItems={loop.mediaItems}
          isNew={isNew}
          redirectTo={redirectTo}
          onClose={this.onClose}
          onCloseExtraPanel={this.onCloseExtraPanel}
          onCloseThirdPanel={this.onCloseThirdPanel}
          showDisplayPlayer={() => showDisplayPlayer(loop.messages)}
          onUpdate={this.onUpdate}
          onRefresh={this.getLoop}
        />

        {loopId && !loop._id && (
          <LoadingContent />
        )}
      </div>
    );
  }
}

LoopSidebar.propTypes = {
  loopId: PropTypes.string,
  /** New Loop */
  isNew: PropTypes.bool,
  /** Add a default location to a new mb */
  locationToAdd: PropTypes.shape({
    locationId: PropTypes.string,
    companyId: PropTypes.string,
  }),
  /** Add a default company to a new mb */
  companyToAdd: PropTypes.shape({
    companyId: PropTypes.string,
    companyName: PropTypes.string,
  }),
  /** Link to redirect to on save or other sidebar close */
  redirectTo: PropTypes.string,
};

LoopSidebar.defaultProps = {
  loopId: null,
  isNew: false,
  /** Add a default location to a new mb */
  locationToAdd: null,
  companyToAdd: null,
  redirectTo: '',
};

const mapDispatchToProps = {
  setPanel: GlobalActions.setPanel,
};

function mapStateToProps(state) {
  return {
    user: state.login.user,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(LoopSidebar);
